// =====================================================================
// myCRM — Sequences, Activity (Upcoming + History), Quotes, Campaigns
// =====================================================================

function NewSequenceDialog({ onClose }) {
  const users = assignableReps();
  const [form, setForm] = useState({ name: "", type: "Outbound", segment: "Mid-market", steps: 9, owner: users[0]?.id || "", status: "Draft" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  async function handleSave() {
    if (!form.name.trim()) { setError("Name is required."); return; }
    setSaving(true); setError(null);
    try {
      await tpAPI.sequences.create({ id: "sq_" + Math.random().toString(36).slice(2, 9), ...form, enrolled: 0, replyRate: 0, meetingRate: 0 });
      onClose();
    } catch(e) { setError(e.message || "Failed to create sequence."); setSaving(false); }
  }
  return (
    <Dialog title="New sequence" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Creating…" : "Create sequence"}</button>
    </>}>
      <div className="form-field"><label>Sequence name *</label>
        <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Enterprise — outbound v3" autoFocus />
      </div>
      <div className="form-row">
        <div className="form-field"><label>Type</label>
          <select value={form.type} onChange={e => set("type", e.target.value)}>
            <option>Outbound</option><option>Inbound</option><option>Nurture</option><option>Re-engagement</option>
          </select>
        </div>
        <div className="form-field"><label>Segment</label>
          <select value={form.segment} onChange={e => set("segment", e.target.value)}>
            <option>SME</option><option>Mid-market</option><option>Enterprise</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Steps</label>
          <input type="number" min="1" max="20" value={form.steps} onChange={e => set("steps", Number(e.target.value))} />
        </div>
        <div className="form-field"><label>Owner</label>
          <select value={form.owner} onChange={e => set("owner", e.target.value)}>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

function NewQuoteDialog({ onClose }) {
  const opps = (D.OPPORTUNITIES || []).filter(o => !o.stage.startsWith("Closed"));
  const [form, setForm] = useState({ oppId: opps[0]?.id || "", acv: "", tcv: "", disc: 0 });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  async function handleSave() {
    if (!form.oppId) return;
    setSaving(true); setError(null);
    try {
      await tpAPI.quotes.create({
        id: "q_" + Math.random().toString(36).slice(2, 9),
        oppId: form.oppId,
        status: "Draft",
        ver: 1,
        sent: null,
        expiry: null,
        acv: form.acv ? Number(form.acv) : null,
        tcv: form.tcv ? Number(form.tcv) : null,
        disc: form.disc ? Number(form.disc) : 0,
        approval: "Pending",
      });
      setDone(true); setTimeout(onClose, 1400);
    } catch (e) {
      setError(e.message || "Failed to create quote.");
      setSaving(false);
    }
  }
  return (
    <Dialog title="New quote" onClose={onClose} footer={!done && <>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !form.oppId}>{saving ? "Creating…" : "Create quote"}</button>
    </>}>
      {done ? <div className="quick-saved">✓ Quote created as Draft</div> : <>
        <div className="form-field"><label>Opportunity</label>
          <select value={form.oppId} onChange={e => set("oppId", e.target.value)}>
            <option value="">— Select opportunity —</option>
            {opps.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div className="form-row">
          <div className="form-field"><label>ACV</label>
            <input type="number" value={form.acv} onChange={e => set("acv", e.target.value)} placeholder="120000" />
          </div>
          <div className="form-field"><label>TCV</label>
            <input type="number" value={form.tcv} onChange={e => set("tcv", e.target.value)} placeholder="360000" />
          </div>
        </div>
        <div className="form-field"><label>Discount %</label>
          <input type="number" min="0" max="100" value={form.disc} onChange={e => set("disc", e.target.value)} />
        </div>
        {error && <div className="form-error">{error}</div>}
      </>}
    </Dialog>
  );
}

function NewCampaignDialog({ onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ name: "", type: "ABM", start: today, end: "", budget: "" });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  async function handleSave() {
    if (!form.name.trim()) { setError("Name is required."); return; }
    setSaving(true); setError(null);
    try {
      await tpAPI.campaigns.create({
        id: "cm_" + Math.random().toString(36).slice(2, 9),
        name: form.name.trim(),
        type: form.type,
        status: "Draft",
        start: form.start || null,
        end:   form.end   || null,
        target: 0, enrolled: 0, mql: 0, sql: 0,
        pipe: 0, infl: 0,
        cost: form.budget ? Number(form.budget) : 0,
      });
      setDone(true); setTimeout(onClose, 1400);
    } catch (e) {
      setError(e.message || "Failed to create campaign.");
      setSaving(false);
    }
  }
  return (
    <Dialog title="New campaign" onClose={onClose} footer={!done && <>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Creating…" : "Create campaign"}</button>
    </>}>
      {done ? <div className="quick-saved">✓ Campaign created as Draft</div> : <>
        <div className="form-field"><label>Campaign name *</label>
          <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Q3 ABM — Enterprise" autoFocus />
        </div>
        <div className="form-row">
          <div className="form-field"><label>Type</label>
            <select value={form.type} onChange={e => set("type", e.target.value)}>
              <option>ABM</option><option>Webinar</option><option>Event</option><option>Content</option><option>Email</option>
            </select>
          </div>
          <div className="form-field"><label>Budget</label>
            <input type="number" value={form.budget} onChange={e => set("budget", e.target.value)} placeholder="25000" />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field"><label>Start date</label>
            <input type="date" value={form.start} onChange={e => set("start", e.target.value)} />
          </div>
          <div className="form-field"><label>End date</label>
            <input type="date" value={form.end} onChange={e => set("end", e.target.value)} />
          </div>
        </div>
        {error && <div className="form-error">{error}</div>}
      </>}
    </Dialog>
  );
}

// Body of the "Sequences" tab on the Outreach page. Exposed (via the
// SequencesList export below) for the legacy /sales/sequences route so
// old bookmarks still resolve to the same list — wrapped in OutreachPage
// when reached via /sales/outreach.
function SequencesTab({ navigate }) {
  const [showNew, setShowNew] = useState(false);
  return (
    <>
      <div className="page-actions" style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>+ New sequence</button>
      </div>
      <div className="card"><table className="tbl tbl-flush">
        <thead><tr><th>Name</th><th>Type</th><th>Segment</th><th>Status</th><th className="right">Steps</th><th className="right">Enrolled</th><th className="right">Reply</th><th className="right">Mtg booked</th><th>Owner</th></tr></thead>
        <tbody>{D.SEQUENCES.length === 0 ? (
          <tr><td colSpan="9" className="muted" style={{ padding: 16 }}>No sequences yet. Click <strong>+ New sequence</strong> to add one.</td></tr>
        ) : D.SEQUENCES.map(s => {
          const st = seqStats(s.id);
          return (
          <tr key={s.id} onClick={() => navigate("sales/sequences/" + s.id)}>
            <td><span className="tbl-link">{s.name}</span></td>
            <td className="muted">{s.type}</td>
            <td><Badge tone="slate">{s.segment}</Badge></td>
            <td><Badge tone={s.status === "Active" ? "green" : "slate"}>{s.status}</Badge></td>
            <td className="right numeric">{s.steps}</td>
            <td className="right numeric">{st.enrolled}</td>
            <td className="right numeric">{Math.round(st.replyRate*100)}%</td>
            <td className="right numeric">{Math.round(st.meetingRate*100)}%</td>
            <td><Avatar user={s.owner} size={20} /></td>
          </tr>
        );})}</tbody>
      </table></div>
      {showNew && <NewSequenceDialog onClose={() => setShowNew(false)} />}
    </>
  );
}
// One enrolled-contact row with an inline, optimistic status change.
function EnrollmentRow({ enr, navigate }) {
  const c = getContact(enr.contactId) || {};
  const [status, setStatus] = useState(enr.status || "Active");
  const [saving, setSaving] = useState(false);
  function change(next) {
    const prev = status;
    setStatus(next); setSaving(true);
    tpAPI.enrollments.update(enr.id, { status: next })
      .catch(() => setStatus(prev))
      .finally(() => setSaving(false));
  }
  return (
    <tr style={{ cursor: "default" }}>
      <td><span className="tbl-link" onClick={() => enr.contactId && navigate("sales/contacts/" + enr.contactId)}>{c.first} {c.last}</span></td>
      <td>{fmtDateShort(enr.enrolledOn)}</td>
      <td>
        <select value={status} disabled={saving} onChange={e => change(e.target.value)} style={{ fontSize: 12 }}>
          <option>Active</option><option>Replied</option><option>Meeting booked</option><option>Exited</option>
        </select>
      </td>
    </tr>
  );
}

function SequenceDetail({ id, navigate }) {
  const s = D.SEQUENCES.find(x => x.id === id);
  const [tab, setTab] = useState("overview");
  if (!s) return <PageHead title="Not found" />;
  const tabs = [{key:"overview",label:"Overview"},{key:"perf",label:"Performance"},{key:"enrolled",label:"Enrolled"}];
  const st = seqStats(s.id);
  const seqEnrollments = (D.ENROLLMENTS || []).filter(e => e.sequenceId === s.id);
  return (
    <>
      <PageHead title={s.name} sub={<>{s.type} · <Badge tone="slate">{s.segment}</Badge> · <Badge tone="green">{s.status}</Badge></>} />
      <Tabs tabs={tabs} current={tab} onChange={setTab} />
      <div className="detail"><div>
        {tab === "overview" && <div className="card"><div className="card-body"><div className="kv">
          <div className="kv-label">Steps</div><div className="kv-value">{s.steps}</div>
          <div className="kv-label">Segment</div><div className="kv-value">{s.segment}</div>
          <div className="kv-label">Owner</div><div className="kv-value">{getUser(s.owner)?.name}</div>
          <div className="kv-label">Enrolled</div><div className="kv-value numeric">{st.enrolled}</div>
          <div className="kv-label">Replied</div><div className="kv-value numeric">{st.replied} · {Math.round(st.replyRate*100)}%</div>
          <div className="kv-label">Meetings booked</div><div className="kv-value numeric">{st.meetings} · {Math.round(st.meetingRate*100)}%</div>
        </div></div></div>}
        {tab === "perf" && <div className="card"><div className="card-body">
          <BarRow label="Enrolled" value={st.enrolled} max={Math.max(st.enrolled, 1)} />
          <BarRow label="Replied" value={st.replied} max={Math.max(st.enrolled, 1)} tone="bar-green" />
          <BarRow label="Booked" value={st.meetings} max={Math.max(st.enrolled, 1)} tone="bar-blue" />
        </div></div>}
        {tab === "enrolled" && <div className="card">
          {seqEnrollments.length === 0 ? (
            <div className="card-body muted">No contacts enrolled yet. Use “Add to sequence” on a contact's page.</div>
          ) : (
            <table className="tbl tbl-flush">
              <thead><tr><th>Contact</th><th>Enrolled on</th><th>Status</th></tr></thead>
              <tbody>{seqEnrollments.map(e => <EnrollmentRow key={e.id} enr={e} navigate={navigate} />)}</tbody>
            </table>
          )}
        </div>}
      </div><RightRail record={{...s, createdBy: s.owner}} /></div>
    </>
  );
}

const ACTIVITY_TYPE_BY_TAB = { calls: "Call", emails: "Email", meetings: "Meeting", tasks: "Task" };

// Body of the "History" tab on the Activity page — log of past logged
// interactions. Subdivided by type (Calls / Emails / Meetings / Tasks).
function HistoryTab({ navigate }) {
  const [tab, setTab] = useState("calls");
  const all = D.ACTIVITIES || [];
  const tabs = [
    { key: "calls",    label: "Calls",    count: all.filter(a => a.type === "Call").length },
    { key: "emails",   label: "Emails",   count: all.filter(a => a.type === "Email").length },
    { key: "meetings", label: "Meetings", count: all.filter(a => a.type === "Meeting").length },
    { key: "tasks",    label: "Tasks",    count: all.filter(a => a.type === "Task").length },
  ];
  const rows = all.filter(a => a.type === ACTIVITY_TYPE_BY_TAB[tab]);
  return (
    <>
      <Tabs tabs={tabs} current={tab} onChange={setTab} />
      <div className="card">
        {rows.length === 0 ? (
          <div className="card-body muted">No {tab} logged yet. Use “Log activity” on any account, contact or opportunity.</div>
        ) : (
          <table className="tbl tbl-flush">
            <thead><tr><th>Subject</th><th>Contact</th><th>Account</th><th>Date</th><th>{tab === "tasks" ? "Priority" : "Outcome"}</th><th>Owner</th></tr></thead>
            <tbody>
              {rows.map(act => {
                const c = getContact(act.contactId) || {};
                const a = getAccount(act.accountId) || {};
                const target = act.oppId ? "sales/opportunities/" + act.oppId
                  : act.contactId ? "sales/contacts/" + act.contactId
                  : act.accountId ? "sales/accounts/" + act.accountId : null;
                return (
                  <tr key={act.id} onClick={() => target && navigate(target)}>
                    <td>{act.direction && <Badge tone="slate">{act.direction}</Badge>} {act.subject}</td>
                    <td>{c.first ? `${c.first} ${c.last}` : <span className="muted">—</span>}</td>
                    <td>{a.name || <span className="muted">—</span>}</td>
                    <td>{fmtDate(act.date)}</td>
                    <td>{act.outcome || act.priority || <span className="muted">—</span>}</td>
                    <td>{act.owner ? <Avatar user={act.owner} size={20} /> : <span className="muted">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function QuotesPage({ navigate }) {
  const [showNew, setShowNew] = useState(false);
  const rows = D.QUOTES || [];
  return (
    <>
      <PageHead title="Quotes" sub={`${rows.length} quotes`} actions={<button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>+ New quote</button>} />
      <div className="card"><table className="tbl tbl-flush">
        <thead><tr><th>Quote ID</th><th>Opportunity</th><th>Status</th><th>v</th><th>Sent</th><th>Expiry</th><th className="right">ACV</th><th className="right">TCV</th><th>Discount</th><th>Approval</th></tr></thead>
        <tbody>{rows.map(q => {
          const o = getOpp(q.oppId);
          const oppName = o ? o.name : q.oppId;
          return (
            <tr key={q.id}>
              <td className="mono">{q.id}</td>
              <td><span className="tbl-link" onClick={() => navigate("sales/opportunities/" + q.oppId)}>{oppName}</span></td>
              <td><Badge tone={q.status === "Accepted" ? "green" : q.status === "Sent" ? "blue" : "slate"}>{q.status}</Badge></td>
              <td>v{q.ver}</td>
              <td>{fmtDateShort(q.sent)}</td>
              <td>{fmtDateShort(q.expiry)}</td>
              <td className="right numeric">{fmtMoney(q.acv)}</td>
              <td className="right numeric">{fmtMoney(q.tcv)}</td>
              <td>{q.disc}%</td>
              <td><Badge tone={q.approval === "Approved" ? "green" : "amber"}>{q.approval}</Badge></td>
            </tr>
          );
        })}</tbody>
      </table></div>
      {showNew && <NewQuoteDialog onClose={() => setShowNew(false)} />}
    </>
  );
}

// Body of the "Campaigns" tab on the Outreach page. Wrapped in
// OutreachPage when reached via /sales/outreach; reached directly via
// the legacy /sales/campaigns route too.
function CampaignsTab({ navigate }) {
  const [showNew, setShowNew] = useState(false);
  const rows = D.CAMPAIGNS || [];
  return (
    <>
      <div className="page-actions" style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>+ New campaign</button>
      </div>
      <div className="card"><table className="tbl tbl-flush">
        <thead><tr><th>Name</th><th>Type</th><th>Status</th><th>Start</th><th className="right">Enrolled</th><th className="right">MQL</th><th className="right">SQL</th><th className="right">Pipe</th><th className="right">Influenced</th><th className="right">Cost</th></tr></thead>
        <tbody>{rows.map(cm => (
          <tr key={cm.id}>
            <td><span className="tbl-link">{cm.name}</span></td>
            <td><Badge tone="slate">{cm.type}</Badge></td>
            <td><Badge tone={cm.status === "Active" ? "green" : "slate"}>{cm.status}</Badge></td>
            <td>{fmtDateShort(cm.start)}</td>
            <td className="right numeric">{cm.enrolled}</td>
            <td className="right numeric">{cm.mql}</td>
            <td className="right numeric">{cm.sql}</td>
            <td className="right numeric">{fmtMoney(cm.pipe)}</td>
            <td className="right numeric">{fmtMoney(cm.infl)}</td>
            <td className="right numeric">{fmtMoney(cm.cost)}</td>
          </tr>
        ))}</tbody>
      </table></div>
      {showNew && <NewCampaignDialog onClose={() => setShowNew(false)} />}
    </>
  );
}

// Bucket tasks by due-date relative to today. Buckets are evaluated in
// order — the first one whose predicate matches wins, so "Today" beats
// "This week" for a task due today. Times are zeroed to local-midnight
// boundaries so a task due at 23:59 today doesn't slip into "Overdue".
function midnight(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function startOfWeek(d) {
  // ISO week: Monday is day 1. Treat Sunday (getDay()=0) as 7 so it lands
  // at the end of the previous week, not the start of a new one.
  const x = midnight(d);
  const day = x.getDay() === 0 ? 7 : x.getDay();
  x.setDate(x.getDate() - (day - 1));
  return x;
}
function bucketForDate(dateStr, now) {
  if (!dateStr) return "nodate";
  const d = midnight(dateStr);
  const today = midnight(now);
  if (d < today) return "overdue";
  if (d.getTime() === today.getTime()) return "today";
  const thisWeekEnd = new Date(startOfWeek(now)); thisWeekEnd.setDate(thisWeekEnd.getDate() + 7); // Mon of next week
  if (d < thisWeekEnd) return "thisWeek";
  const nextWeekEnd = new Date(thisWeekEnd); nextWeekEnd.setDate(nextWeekEnd.getDate() + 7); // Mon two weeks out
  if (d < nextWeekEnd) return "nextWeek";
  return "later";
}

const BUCKET_ORDER = [
  { key: "overdue",  label: "Overdue",   tone: "red"   },
  { key: "today",    label: "Today",     tone: "amber" },
  { key: "thisWeek", label: "This week", tone: "amber" },
  { key: "nextWeek", label: "Next week", tone: "slate" },
  { key: "later",    label: "Later",     tone: "slate" },
  { key: "nodate",   label: "No date",   tone: "slate" },
];

// Body of the "Upcoming" tab on the Activity page — outstanding tasks /
// follow-ups due, bucketed by relative due-date.
function UpcomingTab({ navigate }) {
  const [statusFilter, setStatusFilter] = useState("Open");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const tasks = (D.TASKS || []).filter(t => {
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    if (ownerFilter !== "all" && t.owner !== ownerFilter) return false;
    return true;
  });

  function targetFor(t) {
    if (t.leadId) {
      const l = (D.LEADS || []).find(x => x.id === t.leadId);
      return l
        ? { label: `${l.first} ${l.last} · ${l.company || ""}`.trim(), route: "sales/leads/" + l.id, kind: "Lead" }
        : { label: t.leadId, route: null, kind: "Lead" };
    }
    if (t.accountId) {
      const a = getAccount(t.accountId);
      return a ? { label: a.name, route: "sales/accounts/" + a.id, kind: "Account" } : { label: t.accountId, route: null, kind: "Account" };
    }
    if (t.contactId) {
      const c = getContact(t.contactId);
      return c ? { label: `${c.first} ${c.last}`, route: "sales/contacts/" + c.id, kind: "Contact" } : { label: t.contactId, route: null, kind: "Contact" };
    }
    if (t.oppId) {
      const o = getOpp(t.oppId);
      return o ? { label: o.name, route: "sales/opportunities/" + o.id, kind: "Opp" } : { label: t.oppId, route: null, kind: "Opp" };
    }
    return { label: "—", route: null, kind: "—" };
  }

  function dueTone(dateStr) {
    if (!dateStr) return "slate";
    const days = Math.round((new Date(dateStr).getTime() - Date.now()) / 86400000);
    if (days < 0) return "red";
    if (days <= 7) return "amber";
    return "slate";
  }

  // Partition the filtered list once, then render each bucket. Tasks
  // within a bucket sort by due date asc; "nodate" sorts by id for stability.
  const now = new Date();
  const buckets = Object.fromEntries(BUCKET_ORDER.map(b => [b.key, []]));
  for (const t of tasks) buckets[bucketForDate(t.dueDate, now)].push(t);
  for (const b of BUCKET_ORDER) {
    buckets[b.key].sort((x, y) => {
      if (!x.dueDate) return 1;
      if (!y.dueDate) return -1;
      return x.dueDate.localeCompare(y.dueDate);
    });
  }

  function renderRow(t) {
    const tgt = targetFor(t);
    return (
      <tr key={t.id} onClick={() => tgt.route && navigate(tgt.route)} style={{ cursor: tgt.route ? "pointer" : "default" }}>
        <td><span className={tgt.route ? "tbl-link" : ""}>{t.title}</span></td>
        <td><Badge tone="slate">{tgt.kind}</Badge> <span className="muted">{tgt.label}</span></td>
        <td><Badge tone={dueTone(t.dueDate)}>{t.dueDate ? fmtDateShort(t.dueDate) : "—"}</Badge></td>
        <td>{t.owner ? <Avatar user={t.owner} size={20} /> : <span className="muted">—</span>}</td>
        <td><Badge tone={t.status === "Done" ? "green" : t.status === "Cancelled" ? "slate" : "amber"}>{t.status}</Badge></td>
        <td className="muted" style={{ maxWidth: 280 }}>{t.notes || "—"}</td>
        <td className="right" onClick={e => e.stopPropagation()}>
          {t.status === "Open" && (
            <button className="btn btn-sm" onClick={async () => {
              await tpAPI.tasks.update(t.id, { status: "Done", completedAt: new Date().toISOString().slice(0, 10) });
            }}>Mark done</button>
          )}
        </td>
      </tr>
    );
  }

  return (
    <div className="card">
      <FilterToolbar count={tasks.length} total={(D.TASKS || []).length}>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            <option>Open</option><option>Done</option><option>Cancelled</option>
          </select>
          <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}>
            <option value="all">All owners</option>
            {assignableReps().map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </FilterToolbar>
        {tasks.length === 0 ? (
          <div className="card-body muted">No follow-ups match these filters. Move a lead to <strong>Nurture</strong> with a recall date to create one.</div>
        ) : (
          // Render only buckets that have something to show — empty section
          // headers were a wall of beige across the page in light mode.
          BUCKET_ORDER.filter(b => buckets[b.key].length > 0).map(b => {
            const rows = buckets[b.key];
            return (
              <div key={b.key}>
                <div style={{
                  padding: "8px 16px",
                  borderTop: "1px solid var(--divider)",
                  background: "var(--bg-sunken)",
                  fontSize: 12, fontWeight: 600,
                  color: "var(--text-muted)",
                  display: "flex", alignItems: "center", gap: 8,
                  letterSpacing: ".02em",
                }}>
                  <Badge tone={b.tone} dot>{b.label}</Badge>
                  <span className="muted" style={{ fontWeight: 400 }}>{rows.length}</span>
                </div>
                <table className="tbl tbl-flush">
                  <thead><tr><th>Title</th><th>Target</th><th>Due</th><th>Owner</th><th>Status</th><th>Notes</th><th></th></tr></thead>
                  <tbody>{rows.map(renderRow)}</tbody>
                </table>
              </div>
            );
          })
        )}
    </div>
  );
}

// Activity = pending follow-ups + history of logged interactions. Single
// page with two top-level tabs so the sidebar isn't carrying both. The
// legacy /sales/follow-ups and /sales/activities routes still resolve
// here via initialTab in entry.jsx, so old bookmarks keep working.
function ActivityPage({ navigate, initialTab }) {
  const [tab, setTab] = useState(initialTab === "history" ? "history" : "upcoming");
  const upcomingCount = (D.TASKS || []).filter(t => t.status === "Open").length;
  const historyCount = (D.ACTIVITIES || []).length;
  const tabs = [
    { key: "upcoming", label: "Upcoming", count: upcomingCount },
    { key: "history",  label: "History",  count: historyCount  },
  ];
  return (
    <>
      <PageHead title="Activity"
        sub={tab === "upcoming"
          ? "Outstanding follow-ups and tasks · task-only lens on the calendar"
          : "Historical log of every logged interaction · for upcoming work see Upcoming or the Calendar"}
        actions={<button className="btn btn-sm" onClick={() => navigate("sales/calendar")}>📅 Open calendar →</button>}
      />
      <Tabs tabs={tabs} current={tab} onChange={setTab} />
      {tab === "upcoming" ? <UpcomingTab navigate={navigate} /> : <HistoryTab navigate={navigate} />}
    </>
  );
}

// Combined Sequences + Campaigns landing page. Both used to be their
// own sidebar items; merging them keeps the sales nav shorter while
// reusing the existing list bodies. Legacy /sales/sequences and
// /sales/campaigns routes still resolve here via initialTab in
// entry.jsx, and the per-sequence detail page still lives at
// /sales/sequences/:id (no tab needed there).
function OutreachPage({ navigate, initialTab }) {
  const [tab, setTab] = useState(initialTab === "campaigns" ? "campaigns" : "sequences");
  const seqCount = (D.SEQUENCES || []).length;
  const campCount = (D.CAMPAIGNS || []).length;
  const tabs = [
    { key: "sequences", label: "Sequences", count: seqCount },
    { key: "campaigns", label: "Campaigns", count: campCount },
  ];
  return (
    <>
      <PageHead title="Outreach"
        sub={tab === "sequences"
          ? "Multi-step outbound cadences and their enrolled contacts"
          : "Marketing campaigns and their pipeline contribution"} />
      <Tabs tabs={tabs} current={tab} onChange={setTab} />
      {tab === "sequences" ? <SequencesTab navigate={navigate} /> : <CampaignsTab navigate={navigate} />}
    </>
  );
}

window.SequencesTab = SequencesTab; window.SequenceDetail = SequenceDetail;
window.QuotesPage = QuotesPage; window.CampaignsTab = CampaignsTab;
window.OutreachPage = OutreachPage;
window.ActivityPage = ActivityPage;
